import { Article, Demo, Tutorial } from '@app/components/Guide/types/guide.types'
import apiClient from '@app/config/axios.config'

export type TutorialOrDemoArticle =
  | (Tutorial & { contentType: 'tutorials' })
  | (Demo & { id: number; contentType: 'demos' })

export const fetchTutorialsAndDemosArticles = async (): Promise<TutorialOrDemoArticle[]> => {
  const [tutorialsResponse, demosResponse] = await Promise.all([
    apiClient.get('/cms/tutorials', {
      params: {
        populate: {
          sub_tabs: {
            fields: ['slug', 'title'],
            populate: {
              main_topic: {
                fields: ['slug', 'title'],
              },
            },
          },
        },
        sort: ['createdAt:desc'],
        pagination: {
          pageSize: 3,
        },
      },
    }),
    apiClient.get('/cms/demos', {
      params: {
        populate: {
          sub_tabs: {
            fields: ['slug', 'title'],
            populate: {
              main_topic: {
                fields: ['slug', 'title'],
              },
            },
          },
        },
        sort: ['createdAt:desc'],
        pagination: {
          pageSize: 3,
        },
      },
    }),
  ])

  const tutorials = tutorialsResponse.data.data.map((tutorial: Tutorial) => ({
    ...tutorial,
    contentType: 'tutorials' as const,
  }))

  const demos = demosResponse.data.data.map((demo: Demo & { id: number }) => ({
    ...demo,
    contentType: 'demos' as const,
  }))

  return [...tutorials, ...demos]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 6)
}

export const fetchRecentArticles = async (): Promise<Article[]> => {
  const response = await apiClient.get('/cms/articles', {
    params: {
      populate: {
        sub_tabs: {
          fields: ['slug', 'title'],
          populate: {
            main_topic: {
              fields: ['slug', 'title'],
            },
          },
        },
      },
      sort: ['createdAt:desc'],
      pagination: {
        pageSize: 6,
      },
    },
  })

  return response.data.data
}

export type MainTopicSubTabWithArticles = {
  id: number
  documentId?: string
  title: string
  slug: string
  main_topic?: {
    id: number
    documentId?: string
    title: string
    slug: string
    description?: string | null
  }
  articles?: Article[]
}

export const fetchMainTopicSubTabsWithArticles = async (
  mainTopicSlug: string,
): Promise<MainTopicSubTabWithArticles[]> => {
  const slug = encodeURIComponent(mainTopicSlug)
  const response = await apiClient.get(
    `/cms/sub-tabs?filters[main_topic][slug][$eq]=${slug}` +
      `&fields[0]=title` +
      `&fields[1]=slug` +
      `&populate[main_topic][fields][0]=title` +
      `&populate[main_topic][fields][1]=slug` +
      `&populate[main_topic][fields][2]=description` +
      `&populate[articles][fields][0]=title` +
      `&populate[articles][fields][1]=slug` +
      `&populate[articles][fields][2]=description` +
      `&populate[articles][fields][3]=is_external` +
      `&populate[articles][fields][4]=external_link` +
      `&populate[articles][populate][hero_image][fields][0]=url` +
      `&populate[articles][populate][hero_image][fields][1]=alternativeText` +
      `&sort=order:asc`,
  )

  return response.data.data
}

export const fetchTutorialsContent = async (): Promise<Tutorial[]> => {
  const response = await apiClient.get(
    `/cms/tutorials?fields[0]=title` +
      `&fields[1]=slug` +
      `&fields[2]=summary` +
      `&fields[3]=arcadeLink` +
      `&populate[thumbnail][fields][0]=url` +
      `&populate[thumbnail][fields][1]=alternativeText` +
      `&populate[sub_tabs][fields][0]=title` +
      `&populate[sub_tabs][fields][1]=slug` +
      `&populate[sub_tabs][populate][main_topic][fields][0]=title` +
      `&populate[sub_tabs][populate][main_topic][fields][1]=slug` +
      `&sort=createdAt:desc`,
  )

  return response.data.data
}

export const fetchDemosContent = async (): Promise<Demo[]> => {
  const response = await apiClient.get(
    `/cms/demos?populate[thumbnail][fields][0]=url` +
      `&populate[thumbnail][fields][1]=alternativeText` +
      `&populate[sub_tabs][fields][0]=title` +
      `&populate[sub_tabs][fields][1]=slug` +
      `&populate[sub_tabs][populate][main_topic][fields][0]=title` +
      `&populate[sub_tabs][populate][main_topic][fields][1]=slug` +
      `&sort=createdAt:desc`,
  )

  return response.data.data
}
